import './index.scss';
import React, { useState, useRef, useEffect, Children, cloneElement } from 'react';
import { debounce } from '@umu/umu-util';
import { useMethods } from '@umu/umu-react-hooks';
import { Popover } from '@umu/design-base';
import { lang } from '@umu/ukit-lang';
export * from './sortBox';

interface MoreTabsProps {
    useDrag?: boolean;
    children: React.ReactNode[];
    dropdownCls?: string;
    defaultActiveIndex?: number;
    renderMoreBtn?: (hideCount: number) => React.ReactNode | string;
    onTabChange?: (index: number) => void;
    renderDropdownTab?: (child: React.ReactNode, idx: number) => React.ReactNode;
}

export const MoreTabs: React.FC<MoreTabsProps> = ({
    children,
    defaultActiveIndex = 0,
    dropdownCls = '',
    onTabChange,
    renderMoreBtn,
    renderDropdownTab,
}) => {
    const childArray = Children.toArray(children);
    const [isReady, setIsReady] = useState(false);
    const [tabWidths, setTabWidths] = useState<number[]>([]);
    const [hiddenTabsCount, setHiddenTabsCount] = useState(0);
    const [activeIndex, setActiveIndex] = useState(defaultActiveIndex);
    const [visibleTabs, setVisibleTabs] = useState<React.ReactChild[]>([]);
    const [menuItemDropdownOpen, setMenuItemDropdownOpen] = useState<boolean>(false);

    const tabTotalWidth = useRef(0);
    const leftHideCount = useRef(0);
    const moreBtnWidth = useRef(100);
    const containerRef = useRef<HTMLDivElement>(null);
    const measuringRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        defaultActiveIndex !== void 0 && setActiveIndex(defaultActiveIndex);
    }, [defaultActiveIndex]);

    useEffect(() => {
        const childElements = Array.from(measuringRef.current!.children);
        let totalWidth = 0;
        const widths = childElements.map((el) => {
            totalWidth += el.clientWidth;
            return el.clientWidth;
        });
        setTabWidths(widths);
        tabTotalWidth.current = totalWidth;
    }, [children]);

    const { calculateVisibleTabs } = useMethods({
        calculateVisibleTabs() {
            if (!containerRef.current || tabWidths.length === 0) {
                leftHideCount.current = 0;
                return;
            }

            if (measuringRef.current!.clientWidth >= tabTotalWidth.current) {
                moreBtnWidth.current = 0;
                leftHideCount.current = 0;
                setHiddenTabsCount(0);
                setVisibleTabs(childArray as React.ReactChild[]);
                return;
            }

            const containerWidth = containerRef.current.clientWidth;
            const remainingWidth = containerWidth - moreBtnWidth.current;

            let visibleCount = 0;
            let totalWidth = 0;

            const leftPart = childArray.slice(0, activeIndex + 1);
            const rightPart = childArray.slice(activeIndex + 1);
            const result = leftPart.reduceRight<React.ReactNode[]>((acc, cur, index) => {
                const currentWidth = tabWidths[index];
                if (totalWidth + currentWidth <= remainingWidth) {
                    totalWidth += currentWidth;
                    visibleCount++;
                    acc.unshift(cur);
                }
                return acc;
            }, []);
            leftHideCount.current = leftPart.length - visibleCount;
            if (totalWidth < remainingWidth) {
                for (const child of rightPart) {
                    const currentWidth = tabWidths[childArray.indexOf(child)];
                    if (totalWidth + currentWidth <= remainingWidth) {
                        totalWidth += currentWidth;
                        visibleCount++;
                        result.push(child);
                    } else {
                        break;
                    }
                }
            }

            setVisibleTabs(result as React.ReactChild[]);
            setHiddenTabsCount(childArray.length - visibleCount);
        },
    });

    useEffect(() => {
        const handleResize = debounce(() => {
            calculateVisibleTabs();
        }, 100);

        window.addEventListener('resize', handleResize);
        setIsReady(true);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        calculateVisibleTabs();
    }, [activeIndex, children, tabWidths]);

    const handleTabChange = (index: number) => {
        setActiveIndex(index);
        onTabChange?.(index);
    };

    const renderVisibleTabs = () => {
        if (!isReady) {
            return null;
        }

        return visibleTabs.map((child, idx) => (
            <div onClick={() => handleTabChange(leftHideCount.current + idx)} key={idx}>
                {cloneElement(child as React.ReactElement)}
            </div>
        ));
    };

    const onDrawerItemClick = (e: React.MouseEvent, idx: number) => {
        handleTabChange(idx);
        setMenuItemDropdownOpen(false);
        e.stopPropagation();
    };

    const renderDropDown = () => {
        return (
            <div className={`flex-column max-h-50vh overflow-auto ${dropdownCls}`}>
                {children.map((child: React.ReactNode, idx: number) => {
                    return (
                        <div key={idx} onClick={(e) => onDrawerItemClick(e, idx)}>
                            {renderDropdownTab
                                ? renderDropdownTab(child, idx)
                                : cloneElement(child as React.ReactElement)}
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="more-tabs">
            <div ref={containerRef} className="more-tabs__container flex-start-center">
                {renderVisibleTabs()}
                {hiddenTabsCount > 0 && (
                    <Popover
                        trigger="click"
                        placement="bottomRight"
                        content={renderDropDown()}
                        onOpenChange={(open: boolean) => {
                            !open && setMenuItemDropdownOpen(false);
                        }}
                        open={menuItemDropdownOpen}
                        overlayClassName="more-tabs__drawer"
                    >
                        <button
                            className="p-6-8 shrink-0 space-nowrap border-radius-4 hover:bg-color-f5f5f5"
                            ref={(node) => node && (moreBtnWidth.current = node.clientWidth)}
                            onClick={(e) => {
                                e.stopPropagation();
                                setMenuItemDropdownOpen(true);
                            }}
                        >
                            {renderMoreBtn
                                ? renderMoreBtn(hiddenTabsCount)
                                : lang('其他{$0{0= 个,1= 个}}', hiddenTabsCount) + '...'}
                        </button>
                    </Popover>
                )}
            </div>

            <div ref={measuringRef} className="flex-start-center h-0 overflow-hidden">
                {children}
            </div>
        </div>
    );
};
